import { Icon, Spinner, Stack } from '@chakra-ui/react';
import {
  AutoPaymentCollectionTimingEnum,
  InvoiceSummaryResp,
  MassEmailCustomerInvoicesResults,
} from '@monetize/types/app';
import { memo } from 'react';
import { MdCheck, MdCheckCircle, MdCreditCard, MdError } from 'react-icons/md';
import { MBox, MFlex, MIcon, MText } from '../../../../components/Monetize';
import { ItemWithLinkInNewWindow } from '../../../../components/Monetize/ItemWithLinkInNewWindow';
import {
  getAccountDetailRoute,
  getInvoiceDetailRoute,
} from '../../../../constants/routes';

interface EmailListItemContentProps {
  row: InvoiceSummaryResp;
  result?: MassEmailCustomerInvoicesResults;
  isLoading: boolean;
}

interface AutoPayStatusProps {
  result?: MassEmailCustomerInvoicesResults;
}

const AutoPayStatus = ({ result }: AutoPayStatusProps) => {
  if (!result) {
    return (
      <MFlex alignItems="center" gap={1}>
        <MIcon as={MdCreditCard} boxSize={3} color="tPurple.base" />
        <MText fontSize="xs" color="tPurple.base">
          Auto-payment will be attempted on send
        </MText>
      </MFlex>
    );
  }

  if (result.paymentCollectionAttempted && result.paymentCollected) {
    return (
      <MFlex alignItems="center" gap={1}>
        <MIcon as={MdCheck} boxSize={3} color="tGreen.approval" />
        <MText fontSize="xs" color="tGreen.approval">
          Auto-payment collected successfully
        </MText>
      </MFlex>
    );
  }

  if (result.paymentCollectionAttempted && !result.paymentCollected) {
    return (
      <MFlex alignItems="center" gap={1}>
        <MIcon as={MdError} boxSize={3} color="tRed.base" />
        <MText fontSize="xs" color="tRed.base">
          Auto-payment collection failed
        </MText>
      </MFlex>
    );
  }

  return null;
};

export const EmailListItemContent = memo(function EmailListItemContent({
  row,
  result,
  isLoading,
}: EmailListItemContentProps) {
  const showAutoPay =
    row.billGroup.autoPaymentCollectionTiming ===
      AutoPaymentCollectionTimingEnum.INVOICE_SEND &&
    row.billGroup.collectInvoiceBalanceAutomatically === true &&
    !!row.billGroup.defaultPaymentMethodId;

  return (
    <MFlex w="100%">
      <MBox pt={1} w="20px">
        {result && (
          <Icon
            as={result.success ? MdCheckCircle : MdError}
            color={result.success ? 'tGreen.approval' : 'tRed.base'}
            w="4"
            h="4"
            mr="1"
          />
        )}
        {!result && isLoading && <Spinner size="xs" />}
      </MBox>
      <Stack direction="column" gap="1" flex="1">
        <ItemWithLinkInNewWindow url={getInvoiceDetailRoute(row.id)}>
          <MText fontWeight="bold">{row.invoiceNumber || row.id}</MText>
        </ItemWithLinkInNewWindow>
        <ItemWithLinkInNewWindow url={getAccountDetailRoute(row.account.id)}>
          <MText fontWeight="bold">{row.account.accountName}</MText>
        </ItemWithLinkInNewWindow>
        <MText>{row.billingContact.email}</MText>
        {Array.isArray(row.billGroup.ccEmails) &&
          row.billGroup.ccEmails.length > 0 && (
            <MText>CC: {row.billGroup.ccEmails.join(', ')}</MText>
          )}
        {showAutoPay && <AutoPayStatus result={result} />}
        {result?.error && (
          <MText fontSize="xs" color="tRed.base">
            {result.error}
          </MText>
        )}
      </Stack>
    </MFlex>
  );
});
